import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

export const SectorSelect = ({ 
  value, 
  onChange, 
  sectors = {}, 
  placeholder = "Sélectionnez un secteur",
  required = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Find the selected sector name
  const getSelectedName = () => {
    if (!value) return null;
    for (const [category, sectorsList] of Object.entries(sectors)) {
      const found = sectorsList.find(s => s.id === value);
      if (found) return found.name;
    }
    return null;
  };

  const selectedName = getSelectedName();

  // Filter sectors based on search
  const filteredSectors = {};
  Object.entries(sectors).forEach(([category, sectorsList]) => {
    const filtered = sectorsList.filter(sector => 
      sector.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      filteredSectors[category] = filtered;
    }
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when opening
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (sectorId) => {
    onChange(sectorId);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      {/* Hidden input for form validation */}
      <input 
        type="hidden" 
        value={value} 
        required={required}
      />
      
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-full h-10 px-3 rounded-md border border-input bg-background text-sm text-left",
          "flex items-center justify-between",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
          "hover:bg-secondary/50 transition-colors",
          !selectedName && "text-muted-foreground"
        )}
        data-testid="sector-select-trigger"
      >
        <span className="truncate">
          {selectedName || placeholder}
        </span>
        <ChevronDown 
          size={18} 
          className={cn(
            "text-muted-foreground transition-transform flex-shrink-0 ml-2",
            isOpen && "rotate-180"
          )} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute z-[100] mt-1 w-full bg-card border border-border rounded-lg shadow-elevated max-h-[300px] overflow-hidden"
          style={{ minWidth: '280px' }}
        >
          {/* Search Input */}
          <div className="p-2 border-b border-border sticky top-0 bg-card">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher un secteur..."
                className="w-full h-9 pl-9 pr-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Options */}
          <div className="overflow-y-auto max-h-[240px]">
            {Object.keys(filteredSectors).length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">
                Aucun secteur trouvé
              </div>
            ) : (
              Object.entries(filteredSectors).map(([category, sectorsList]) => (
                <div key={category}>
                  {/* Category Header */}
                  <div className="px-3 py-2 bg-secondary/50 text-xs font-semibold text-muted-foreground uppercase tracking-wider sticky top-0">
                    {category}
                  </div>
                  {/* Sector Options */}
                  {sectorsList.map((sector) => (
                    <button
                      key={sector.id}
                      type="button"
                      onClick={() => handleSelect(sector.id)}
                      className={cn(
                        "w-full px-3 py-2 text-sm text-left flex items-center justify-between",
                        "hover:bg-primary/10 transition-colors",
                        value === sector.id && "bg-primary/10 text-primary font-medium"
                      )}
                    >
                      <span>{sector.name}</span>
                      {value === sector.id && (
                        <Check size={16} className="text-primary flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectorSelect;
